import React, { useState, useEffect } from 'react';
import { User, Mail, Phone, Home, CreditCard, Calendar, Building } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import mitgliedApi from '../services/mitgliedApiProxy';
import { useToast } from '../hooks/use-toast';
import { BOOKING_URL } from '../config/booking';

/**
 * Berechnet, ob eine Person anhand des Geburtsdatums minderjährig ist (unter 18).
 * @param {string} birthday - Geburtsdatum im Format YYYY-MM-DD
 * @returns {boolean}
 */
const isMinor = (birthday) => {
  if (!birthday) return false;
  const birthDate = new Date(birthday);
  if (Number.isNaN(birthDate.getTime())) return false;
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age < 18;
};

const MembershipRegistrationDialog = ({ plan, isOpen, onClose }) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    street: '',
    city: '',
    email: '',
    mobile: '',
    phone: '',
    birthday: '',
    card: '',
    iban: '',
    bic: '',
    sepaGroup: 1,
  });

  // Vertragsvorlagen laden und automatisch matchen
  useEffect(() => {
    if (isOpen && plan) {
      loadTemplates();
    }
  }, [isOpen, plan]);

  const loadTemplates = async () => {
    try {
      const templatesData = await mitgliedApi.getTemplates();
      setTemplates(templatesData);
      
      // Finde das passende Template anhand des apiName
      if (plan && templatesData.length > 0) {
        const matchingTemplate = templatesData.find(t => 
          t.name === plan.apiName
        );
        
        if (matchingTemplate) {
          setSelectedTemplateId(matchingTemplate.id);
          console.log('Template gefunden:', matchingTemplate.name, 'ID:', matchingTemplate.id);
        } else {
          console.warn('Kein passendes Template gefunden für:', plan.apiName);
          console.log('Verfügbare Templates:', templatesData.map(t => t.name));
        }
      }
    } catch (error) {
      console.error('Fehler beim Laden der Vorlagen:', error);
      toast({
        variant: 'destructive',
        title: 'Fehler',
        description: 'Vertragsvorlagen konnten nicht geladen werden.',
      });
    }
  };



  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Minderjährig + Special-Vertrag → Buchung im Studio erforderlich
  const showMinorNotice = plan?.isSpecial && isMinor(formData.birthday);
  const billingPeriod = plan?.period || 'Monat';
  const pricingText = plan?.summerPromo
    ? `Beitrag: €0 / ${billingPeriod} (erste 12 Wochen), danach €${plan.price} / ${billingPeriod}`
    : `Beitrag: €${plan?.price} / ${billingPeriod}`;

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validierung: Template muss gefunden worden sein
    if (!selectedTemplateId) {
      toast({
        variant: 'destructive',
        title: 'Fehler',
        description: 'Keine passende Vertragsvorlage gefunden. Bitte kontaktieren Sie uns direkt.',
      });
      return;
    }

    // Minderjährige bei Special-Angeboten zur Terminbuchung leiten
    if (showMinorNotice) {
      toast({
        variant: 'destructive',
        title: 'Buchung im Studio erforderlich',
        description: 'Bei minderjährigen Personen ist eine Bestätigung eines Erziehungsberechtigten vor Ort nötig. Bitte buchen Sie einen Termin.',
      });
      return;
    }

    // Validierung: PLZ (5-stellig) muss im Feld "PLZ und Stadt" enthalten sein
    if (!/\b\d{5}\b/.test(formData.city)) {
      toast({
        variant: 'destructive',
        title: 'PLZ fehlt',
        description: 'Bitte geben Sie eine gültige 5-stellige Postleitzahl an, z.B. "85283 Wolnzach".',
      });
      return;
    }

    setLoading(true);
    try {
      // Athletics erwartet ein einzelnes "name"-Feld und interpretiert das erste
      // Wort als Nachname. Daher "Nachname Vorname" senden, damit Vor- und
      // Nachname im System korrekt (nicht vertauscht) ankommen.
      const { firstName, lastName, ...rest } = formData;
      const memberData = {
        ...rest,
        name: `${lastName.trim()} ${firstName.trim()}`.trim(),
        templateId: selectedTemplateId,
        contractBegin: new Date().toISOString().split('T')[0], // Heutiges Datum
      };

      console.log('Sende Mitgliedsdaten:', memberData);
      const response = await mitgliedApi.createMember(memberData);

      toast({
        title: 'Herzlich Willkommen!',
        description: `Ihre Mitgliedschaft wurde erfolgreich erstellt. Ihre Mitgliedsnummer ist: ${response.id}`,
      });

      // Formular zurücksetzen
      setFormData({
        firstName: '',
        lastName: '',
        street: '',
        city: '',
        email: '',
        mobile: '',
        phone: '',
        birthday: '',
        card: '',
        iban: '',
        bic: '',
        sepaGroup: 1,
      });
      onClose();
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Registrierung fehlgeschlagen',
        description: error.response?.data?.error || 'Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold">
            Mitgliedschaft beantragen
          </DialogTitle>
          <DialogDescription>
            {plan && (
              <div>
                <p className="mb-2">
                  Sie haben sich für "{plan.displayName || plan.name}" ({plan.duration}) entschieden.
                </p>
                <p className="text-sm">
                  {pricingText}{plan.setupFee ? ` | Aufnahmegebühr: €${plan.setupFee}` : ''}
                </p>
                {selectedTemplateId && (
                  <p className="text-xs text-green-600 mt-1">
                    ✓ Vertragsvorlage gefunden
                  </p>
                )}
                {!selectedTemplateId && templates.length > 0 && (
                  <p className="text-xs text-orange-600 mt-1">
                    ⚠ Vertragsvorlage wird gesucht...
                  </p>
                )}
                <p className="mt-2">
                  Füllen Sie das Formular aus, um Ihre Mitgliedschaft zu beantragen.
                </p>
              </div>
            )}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Persönliche Daten */}
          <div className="space-y-4">
            <h3 className="font-semibold text-lg">Persönliche Daten</h3>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="firstName">Vorname *</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    id="firstName"
                    placeholder="Max"
                    value={formData.firstName}
                    onChange={(e) => handleChange('firstName', e.target.value)}
                    className="pl-10"
                    required
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="lastName">Nachname *</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    id="lastName"
                    placeholder="Mustermann"
                    value={formData.lastName}
                    onChange={(e) => handleChange('lastName', e.target.value)}
                    className="pl-10"
                    required
                  />
                </div>
              </div>

              <div className="col-span-2">
                <Label htmlFor="birthday">Geburtsdatum *</Label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    id="birthday"
                    type="date"
                    value={formData.birthday}
                    onChange={(e) => handleChange('birthday', e.target.value)}
                    className="pl-10"
                    required
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Adresse */}
          <div className="space-y-4">
            <h3 className="font-semibold text-lg">Adresse</h3>
            
            <div>
              <Label htmlFor="street">Straße und Hausnummer *</Label>
              <div className="relative">
                <Home className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  id="street"
                  placeholder="Musterstraße 12a"
                  value={formData.street}
                  onChange={(e) => handleChange('street', e.target.value)}
                  className="pl-10"
                  required
                />
              </div>
            </div>

            <div>
              <Label htmlFor="city">PLZ und Stadt *</Label>
              <div className="relative">
                <Home className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  id="city"
                  placeholder="85283 Wolnzach"
                  value={formData.city}
                  onChange={(e) => handleChange('city', e.target.value)}
                  className="pl-10"
                  required
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Bitte mit 5-stelliger Postleitzahl, z.B. "85283 Wolnzach".
              </p>
            </div>
          </div>

          {/* Kontaktdaten */}
          <div className="space-y-4">
            <h3 className="font-semibold text-lg">Kontaktdaten</h3>
            
            <div>
              <Label htmlFor="email">E-Mail *</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  id="email"
                  type="email"
                  placeholder="max.mustermann@beispiel.de"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  className="pl-10"
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="mobile">Mobiltelefon *</Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    id="mobile"
                    type="tel"
                    placeholder="0171 1234567"
                    value={formData.mobile}
                    onChange={(e) => handleChange('mobile', e.target.value)}
                    className="pl-10"
                    required
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="phone">Festnetz</Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    id="phone"
                    type="tel"
                    placeholder="Optional"
                    value={formData.phone}
                    onChange={(e) => handleChange('phone', e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Zahlungsdaten */}
          <div className="space-y-4">
            <h3 className="font-semibold text-lg">Zahlungsdaten</h3>
            
            <div>
              <Label htmlFor="iban">IBAN *</Label>
              <div className="relative">
                <CreditCard className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  id="iban"
                  placeholder="DE89 3704 0044 0532 0130 00"
                  value={formData.iban}
                  onChange={(e) => handleChange('iban', e.target.value)}
                  className="pl-10"
                  required
                />
              </div>
            </div>

            <div>
              <Label htmlFor="bic">BIC *</Label>
              <div className="relative">
                <CreditCard className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  id="bic"
                  placeholder="COBADEFFXXX"
                  value={formData.bic}
                  onChange={(e) => handleChange('bic', e.target.value)}
                  className="pl-10"
                  required
                />
              </div>
            </div>

            <div>
              <Label htmlFor="card">Kartennummer (optional)</Label>
              <div className="relative">
                <CreditCard className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  id="card"
                  placeholder="z.B. RFID-Code"
                  value={formData.card}
                  onChange={(e) => handleChange('card', e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
          </div>

          {/* Hinweis */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-800">
              <strong>Hinweis:</strong> Mit dem Absenden dieses Formulars beantragen Sie eine Mitgliedschaft 
              bei POSITIV Wolnzach. Wir werden Ihre Daten prüfen und uns innerhalb von 24 Stunden bei Ihnen melden.
            </p>
          </div>

          {/* Hinweis für minderjährige Personen bei Special-Angeboten */}
          {showMinorNotice && (
            <div className="bg-orange-50 border border-orange-300 rounded-lg p-4 space-y-3">
              <p className="text-sm text-orange-900">
                <strong>Hinweis:</strong> Dieser Vertrag ist bei minderjährigen Personen nur wirksam,
                wenn ein Erziehungsberechtigter vor Ort eine entsprechende Bestätigung unterschreibt.
                Bitte buchen Sie dafür einen Termin bei uns im Studio.
              </p>
              <a
                href={BOOKING_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center rounded-md bg-orange-600 px-4 py-2 text-sm font-semibold text-white hover:bg-orange-700 transition-colors"
              >
                Termin im Studio buchen
              </a>
            </div>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={loading}
            >
              Abbrechen
            </Button>
            <Button type="submit" disabled={loading || showMinorNotice}>
              {loading ? 'Wird verarbeitet...' : 'Mitgliedschaft beantragen'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default MembershipRegistrationDialog;
